import React, { forwardRef, useCallback } from 'react';
import { stripTags, stripFootnotes, replaceSpecialChars } from 'Utils';
import PropTypes from 'prop-types';
import styles from './styles.scss';
import classnames from 'classnames/bind';

export const headlinePropTypes = {
  additionalProps: PropTypes.object,
  additionalAriaLabelText: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
  forceUppercase: PropTypes.bool,
  level: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']).isRequired,
  levelVisually: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
};

const cx = classnames.bind(styles);

const Typography = () => <span />;

Typography.Headline = forwardRef(
  (
    {
      additionalProps,
      additionalAriaLabelText,
      ariaLabel,
      className,
      forceUppercase,
      level: Tag,
      levelVisually,
      value,
    },
    ref
  ) => {
    const getAdditionalAriaLabelText = useCallback(
      (additionalAriaLabelText) => {
        const additionalAriaLabelTextConcat = Array.isArray(additionalAriaLabelText)
          ? additionalAriaLabelText.join(' ')
          : additionalAriaLabelText;

        return additionalAriaLabelTextConcat
          ? ' - ' + stripTags(replaceSpecialChars(additionalAriaLabelTextConcat))
          : '';
      },
      [additionalAriaLabelText]
    );

    const getAriaLabel = useCallback(
      (ariaLabel, additionalAriaLabelText, forceUppercase, value) => {
        const formatedAdditionalAriaLabelText = getAdditionalAriaLabelText(additionalAriaLabelText);

        if (ariaLabel) {
          return ariaLabel + formatedAdditionalAriaLabelText;
        }

        if (typeof value === 'object') {
          console.error('prop.value is a node! Therefore, prop.ariaLabel must be set manually.');
          return undefined;
        }

        const text = stripTags(replaceSpecialChars(stripFootnotes(value).trim()));
        const isSpecialCase = text.toUpperCase().includes('MAZDA6E');
        if (text === text.toUpperCase() || isSpecialCase) {
          return text.toLowerCase() + formatedAdditionalAriaLabelText;
        }

        if (forceUppercase || formatedAdditionalAriaLabelText !== '') {
          return text + formatedAdditionalAriaLabelText;
        }

        return undefined;
      },
      [ariaLabel, additionalAriaLabelText, forceUppercase, value]
    );

    if (!value) {
      return null;
    }

    let visualContent;
    if (typeof value === 'string') {
      visualContent = {
        dangerouslySetInnerHTML: { __html: value },
      };
    } else {
      visualContent = {
        children: value,
      };
    }

    return (
      <Tag
        ref={ref}
        aria-label={getAriaLabel(ariaLabel, additionalAriaLabelText, forceUppercase, value)}
        className={cx(
          className,
          styles.typography,
          forceUppercase ? styles.forceUppercase : styles.initialCase,
          levelVisually ? styles[`typographyHeadline${levelVisually}`] : undefined
        )}
        {...visualContent}
        {...additionalProps}
      />
    );
  }
);

Typography.Headline.propTypes = headlinePropTypes;

export default Typography;
